/**
 * Utilities for handling localized facet values with IDs.
 *
 * Algolia facet values contain both localized labels and language-agnostic IDs:
 * - Most facets: "{localized_label} | {ID}" (e.g., "Edelstahl | STSTL")
 * - Type facet: "{ID} | {localized_label}" (e.g., "Men | Herren")
 *
 * We store IDs in URL params for cross-language persistence, and look up
 * localized labels for display.
 *
 * ## ID Availability by Product Type
 *
 * **IMPORTANT**: Not all facets in the Algolia index have IDs. This affects
 * cross-language URL persistence - filters without IDs won't persist when
 * switching languages.
 *
 * ### Watches
 * - `case`: "Stainless steel | STSTL" → ID: "STSTL" ✓
 * - `dial_color`: "Black | V-DIAL-COLOR-5686" → ID: "V-DIAL-COLOR-5686" ✓
 * - `type`: "Men | Herren" → ID: "Men" (ID first, label second) ✓
 * - `bracelet_category_color`: "Blue | CATEGORY-COLOR-BLUE" (or V-STRAP-COLOR-*) → ID last ✓
 * - `bracelet_category_material`: "Leather strap | …" → ID last ✓
 *
 * Note: Algolia may store these as `string | string[]` per locale (multi-value).
 * Faceting flattens arrays to individual "Label | ID" values — same parser as dial_color.
 *
 * ### Eyewear (partial ID support)
 * - `lens_color`: "Blue | V-LENS-COLOR-3001" → ID: "V-LENS-COLOR-3001" ✓
 * - `frame_material`: "Metal & bioplastic..." → NO ID (plain string)
 * - `frame_color`: "Silver & black" → NO ID (plain string)
 *
 * ### Straps (no IDs - data limitation)
 * - `lug_type`: "Straight" (EN) / "Derecho" (ES) → NO ID
 * - `material`: "Alligator leather" → NO ID
 * - `color`: "Black" / "Negro" → NO ID
 * - `buckle`: "Folding clasp" → NO ID
 * - `lug`: Numeric value (e.g., "24") → Works across languages
 *
 * For facets without IDs, the URL stores the localized value directly.
 * When users switch languages, these filters will be cleared because
 * the value won't match the new locale's facet values.
 *
 * To enable cross-language persistence for straps/eyewear, the Algolia
 * data would need to be updated to include IDs in the format "Label | ID".
 */

/**
 * Extract the ID from a facet value.
 *
 * @param value - Full facet value string
 * @param facetName - Name of the facet (determines parsing logic)
 * @returns The extracted ID
 *
 * @example
 * extractFacetId("Edelstahl | STSTL", "case") // "STSTL"
 * extractFacetId("Men | Herren", "type") // "Men"
 * extractFacetId("Navitimer", "collection") // "Navitimer" (no ID)
 */
export function extractFacetId(value: string, facetName: string): string {
  // Check if value contains a pipe separator
  if (!value.includes(' | ')) {
    return value; // No ID, return as-is
  }

  const parts = value.split(' | ');

  // Type facet has ID FIRST: "Men | Herren"
  if (facetName === 'type') {
    return parts[0].trim();
  }

  // All other facets have ID LAST: "Edelstahl | STSTL"
  return parts[parts.length - 1].trim();
}

/**
 * Extract the localized label from a facet value.
 *
 * @param value - Full facet value string
 * @param facetName - Name of the facet (determines parsing logic)
 * @returns The localized label
 *
 * @example
 * extractFacetLabel("Edelstahl | STSTL", "case") // "Edelstahl"
 * extractFacetLabel("Men | Herren", "type") // "Herren"
 */
export function extractFacetLabel(value: string, facetName: string): string {
  // Check if value contains a pipe separator
  if (!value.includes(' | ')) {
    return value; // No ID, return as-is
  }

  const parts = value.split(' | ');

  // Type facet has label LAST: "Men | Herren"
  if (facetName === 'type') {
    return parts[parts.length - 1].trim();
  }

  // All other facets have label FIRST: "Edelstahl | STSTL"
  return parts[0].trim();
}

/**
 * Parse a facet value into its components.
 *
 * @param value - Full facet value string
 * @param facetName - Name of the facet
 * @returns Object with id and label
 */
export function parseFacetValue(value: string, facetName: string): { id: string; label: string } {
  return {
    id: extractFacetId(value, facetName),
    label: extractFacetLabel(value, facetName),
  };
}

/**
 * Find the full facet value that matches an ID.
 *
 * Used to look up the localized label when we only have the ID from URL params.
 *
 * @param id - The ID to search for
 * @param facetValues - Map of facet values to counts
 * @param facetName - Name of the facet
 * @returns The full facet value string, or null if not found
 */
export function findFacetValueById(
  id: string,
  facetValues: Record<string, number>,
  facetName: string
): string | null {
  // Try exact match first (for facets without IDs like collection)
  if (id in facetValues) {
    return id;
  }

  // Search for a value that contains this ID
  for (const fullValue of Object.keys(facetValues)) {
    const valueId = extractFacetId(fullValue, facetName);
    if (valueId === id) {
      return fullValue;
    }
  }

  return null;
}

/**
 * Get the localized label for an ID from facet values.
 *
 * @param id - The ID from URL params
 * @param facetValues - Map of facet values to counts
 * @param facetName - Name of the facet
 * @returns The localized label, or the ID if not found
 */
export function getLabelForId(
  id: string,
  facetValues: Record<string, number>,
  facetName: string
): string {
  const fullValue = findFacetValueById(id, facetValues, facetName);
  if (fullValue) {
    return extractFacetLabel(fullValue, facetName);
  }
  return id; // Fallback to ID if label not found
}

/**
 * Build a facet filter value that Algolia expects.
 *
 * For Algolia queries, we need to match the full value string.
 * This finds the full value from facet results that matches our ID.
 *
 * @param id - The ID from URL params
 * @param facetValues - Map of facet values to counts
 * @param facetName - Name of the facet
 * @returns The full facet value for Algolia filter, or the ID if not found
 */
export function getAlgoliaFilterValue(
  id: string,
  facetValues: Record<string, number>,
  facetName: string
): string {
  const fullValue = findFacetValueById(id, facetValues, facetName);
  return fullValue || id;
}

/**
 * Check if a facet value matches an ID.
 *
 * @param fullValue - Full facet value string from Algolia
 * @param id - ID from URL params
 * @param facetName - Name of the facet
 * @returns True if the ID matches
 */
export function facetValueMatchesId(fullValue: string, id: string, facetName: string): boolean {
  return extractFacetId(fullValue, facetName) === id;
}
